import { useState } from "react";

type GeoFix = { lat: number; lng: number; accuracy: number };

type GeoState = {
  loading: boolean;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  error: string | null;
};

/**
 * Pinpoint-precision geolocation hook.
 *
 * Uses ``watchPosition`` instead of single-shot ``getCurrentPosition`` so we
 * can collect multiple GPS fixes and pick the most accurate one inside a
 * sampling window. Order of precedence:
 *
 *   1. First fix with ``accuracy <= GOOD_ACCURACY_M`` returns immediately.
 *   2. Otherwise, run for ``MAX_WATCH_MS`` and return the lowest-accuracy
 *      sample collected.
 *   3. Falls back to ``getCurrentPosition`` for browsers that misbehave with
 *      ``watchPosition``.
 *
 * Server stores DECIMAL(9,6) → 6 fractional digits ≈ 11 cm precision. We
 * clamp at the source so callers always receive 6-dp values + an integer
 * accuracy (meters).
 */

const GOOD_ACCURACY_M = 15; // GPS reading we trust as "pinpoint" — return early.
const MAX_WATCH_MS = 12_000; // Hard ceiling on the sampling window.
const FAST_ACCURACY_M = 50; // Acceptable fallback if no good fix in window.

function shape(pos: GeolocationPosition): GeoFix {
  return {
    lat: Number(pos.coords.latitude.toFixed(6)),
    lng: Number(pos.coords.longitude.toFixed(6)),
    accuracy: Math.max(0, Math.round(pos.coords.accuracy ?? 0)),
  };
}

function classifyError(err: GeolocationPositionError): string {
  if (err.code === err.PERMISSION_DENIED) {
    return "Location permission denied. Enable it in browser settings.";
  }
  if (err.code === err.POSITION_UNAVAILABLE) {
    return "Location unavailable. Try again.";
  }
  return "Location request timed out.";
}

export function useGeolocation() {
  const [state, setState] = useState<GeoState>({
    loading: false,
    lat: null,
    lng: null,
    accuracy: null,
    error: null,
  });

  const fetch = (): Promise<GeoFix> =>
    new Promise<GeoFix>((resolve, reject) => {
      if (!navigator.geolocation) {
        const err = "Geolocation not available in this browser";
        setState({ loading: false, lat: null, lng: null, accuracy: null, error: err });
        reject(new Error(err));
        return;
      }
      setState((s) => ({ ...s, loading: true, error: null }));

      const startTime = Date.now();
      let best: GeoFix | null = null;
      let watchId: number | null = null;
      let settled = false;

      const finish = (fix: GeoFix) => {
        if (settled) return;
        settled = true;
        if (watchId !== null) navigator.geolocation.clearWatch(watchId);
        clearTimeout(timeoutHandle);
        setState({ loading: false, ...fix, error: null });
        resolve(fix);
      };

      const fail = (msg: string) => {
        if (settled) return;
        settled = true;
        if (watchId !== null) navigator.geolocation.clearWatch(watchId);
        clearTimeout(timeoutHandle);
        setState({ loading: false, lat: null, lng: null, accuracy: null, error: msg });
        reject(new Error(msg));
      };

      // Hard timeout: take the best sample we have, or fall back to single-shot.
      const timeoutHandle = setTimeout(() => {
        if (best) {
          finish(best);
          return;
        }
        // No sample at all — try single-shot getCurrentPosition as a last
        // resort (some Chromium builds misbehave with watchPosition).
        navigator.geolocation.getCurrentPosition(
          (pos) => finish(shape(pos)),
          (err) => fail(classifyError(err)),
          { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 },
        );
      }, MAX_WATCH_MS);

      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          const fix = shape(pos);
          if (!best || fix.accuracy < best.accuracy) {
            best = fix;
            // Surface intermediate sample to callers (loading=true still) so
            // UI can show "locating… ±42m" if it wants.
            setState({ loading: true, ...fix, error: null });
          }
          // Pinpoint hit — return immediately.
          if (fix.accuracy > 0 && fix.accuracy <= GOOD_ACCURACY_M) {
            finish(fix);
            return;
          }
          // After ~3s, accept any reading <= FAST_ACCURACY_M to avoid making
          // users wait the full window if GPS just won't sharpen.
          if (Date.now() - startTime > 3_000 && fix.accuracy <= FAST_ACCURACY_M) {
            finish(fix);
          }
        },
        (err) => fail(classifyError(err)),
        { enableHighAccuracy: true, timeout: MAX_WATCH_MS, maximumAge: 0 },
      );
    });

  return { ...state, fetch };
}
